import { useEffect, useRef, useState } from "react";
import mermaid from "mermaid";
import { Workflow, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface FlowchartViewerProps {
  content: string;
}

export const FlowchartViewer = ({ content }: FlowchartViewerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!containerRef.current || !content) return;

    mermaid.initialize({
      startOnLoad: true,
      theme: "default",
      securityLevel: "strict",
      flowchart: {
        useMaxWidth: true,
        htmlLabels: true,
        curve: "basis",
      },
    });

    const renderChart = async () => {
      if (!containerRef.current) return;
      
      try {
        containerRef.current.innerHTML = "";
        setIsLoaded(false);
        const { svg } = await mermaid.render(
          `flowchart-${Date.now()}`,
          content
        );
        containerRef.current.innerHTML = svg;
        setIsLoaded(true);
      } catch (error) {
        console.error("Mermaid rendering error:", error);
        containerRef.current.innerHTML = `
          <div class="text-destructive p-4 border border-destructive rounded">
            <p class="font-semibold">Error rendering flowchart</p>
            <p class="text-sm mt-2">Please check your Mermaid syntax</p>
          </div>
        `;
      }
    };

    renderChart();
  }, [content]);

  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.25, 2));
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));
  const handleReset = () => setScale(1);

  return (
    <div className="relative group">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20">
            <Workflow className="h-4 w-4 text-primary" />
          </div>
          <span className="text-sm font-medium text-muted-foreground">Interactive Flowchart</span>
        </div>
        
        {/* Zoom Controls */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomOut}
            disabled={scale <= 0.5}
            className="h-7 w-7 p-0"
          >
            <ZoomOut className="h-3.5 w-3.5" />
          </Button>
          <span className="text-xs text-muted-foreground w-12 text-center">{Math.round(scale * 100)}%</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomIn}
            disabled={scale >= 2}
            className="h-7 w-7 p-0"
          >
            <ZoomIn className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="h-7 w-7 p-0"
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Flowchart Container */}
      <div className="relative overflow-hidden rounded-xl border-2 border-primary/10 bg-gradient-to-br from-background via-background to-primary/5 shadow-lg">
        {/* Decorative corner accents */}
        <div className="absolute top-0 left-0 w-16 h-16 bg-gradient-to-br from-primary/10 to-transparent rounded-br-full" />
        <div className="absolute bottom-0 right-0 w-16 h-16 bg-gradient-to-tl from-primary/10 to-transparent rounded-tl-full" />
        
        {/* Grid pattern overlay */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
            backgroundSize: '24px 24px'
          }}
        />

        {/* Scrollable content */}
        <div className="overflow-auto p-6 max-h-[600px]">
          <div 
            ref={containerRef} 
            className={`mermaid-container min-w-fit transition-all duration-300 ${isLoaded ? 'animate-fade-in' : 'opacity-0'}`}
            style={{ 
              transform: `scale(${scale})`,
              transformOrigin: 'top left'
            }} 
          />
        </div>

        {/* Loading indicator */}
        {!isLoaded && content && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/50">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Workflow className="h-5 w-5 animate-pulse" />
              <span className="text-sm">Rendering flowchart...</span>
            </div>
          </div>
        )}
      </div>

      {/* Hint text */}
      <p className="text-xs text-muted-foreground mt-2 text-center opacity-60">
        Hover to reveal zoom controls • Scroll to pan
      </p>
    </div>
  );
};
